<template>
  <div class="settings standard-layout">

    <v-card class="pa-10">
      <v-card-title>
        {{ $ucfirst($t('screens.settingsScreen', {catalog: "app_mobile"})) }}
      </v-card-title>

      <v-container
          v-if="$store.getters['user/isGranted']('ROLE_ADMIN')"
          class="grey lighten-5 ma-0 mb-8"
          style="max-width: 50%;"
      >
        <v-row no-gutters align="center">
          <v-col cols="6">
            Impersonate
          </v-col>
          <v-col cols="3">
            <v-text-field
                v-model="impersonateUser"
                label="Impersonate User"
                autocomplete="on"
                outlined
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-btn text @click="impersonate" style="margin: 0 0 20px 20px; padding: 25px 10px; float: left;">
              <smart-icon icon="mdi-account-convert" size="16" />
              Impersonate
            </v-btn>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="grey lighten-5 ma-0 mb-8" style="max-width: 50%;">
        <v-row no-gutters align="center">
          <v-col>
            {{ $ucfirst($t('settings.language')) }}
          </v-col>
          <v-col>
            <language-picker @changeLanguage="onChange"/>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="grey lighten-5 ma-0 mb-8" style="max-width: 50%;">
        <v-row no-gutters align="center">
          <v-col cols="12" class="mb-5">
            {{ $ucfirst($t('myMeasurementUnitsSetupScreen.measurementUnits', {catalog: "app_mobile"})) }}
          </v-col>
        </v-row>

        <v-row no-gutters align="center" class="mb-2">
          <v-col cols="1"/>
          <v-col cols="5">
            {{ $t('parameters.LDL', {catalog: "content"}) }}
          </v-col>
          <v-col cols="6">
            <v-select style="width: 200px;" :items="lipidsUnitChoices" v-model="preferredLipidsUnit" item-text="title"
                      item-value="key" outlined dense hide-details/>
          </v-col>
        </v-row>

        <v-row no-gutters align="center">
          <v-col cols="1"/>
          <v-col cols="5">
            {{ $t('general.hba1cGlucose', {catalog: "app_mobile"}) }}
          </v-col>
          <v-col cols="6">
            <v-select style="width: 200px;" :items="glucoseUnitChoices" v-model="preferredGlucoseUnit" item-text="title"
                      item-value="key" outlined dense hide-details/>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div class="my-4 d-flex" :class="['justify-start']">
      <v-btn text @click="goToPreviousPage">
        <v-icon>mdi-chevron-left</v-icon>
        {{ getPreviousButtonText() }}
      </v-btn>
    </div>

  </div>
</template>

<script>
import LanguagePicker from "../components/Parts/Language/LanguagePicker";
import {mapState} from "vuex";
import moment from "moment/moment";
import {fetchPreferredGlucoseUnit} from "dashboard/store/user/actions";
import SmartIcon from "dashboard/templates/components/Parts/SmartIcon.vue";

/**
 * Component Settings.vue
 *
 * @module Settings
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/APP_DASHBOARD/templates/views/Settings.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'Settings',
  components: {SmartIcon, LanguagePicker},
  data() {
    return {
      patientRecordId: null,
      lipidsUnitChoices: [
        {"key": 'mg/dL', "title": this.$t('units.mg/dL', {catalog: 'app_mobile'})},
        {"key": 'mmol/L', "title": this.$t('units.mmol/L', {catalog: 'app_mobile'})},
      ],
      glucoseUnitChoices: [
        {"key": '%', "title": this.$t('units.%', {catalog: 'app_mobile'})},
        {"key": 'mmol/mol', "title": this.$t('units.mmol/mol', {catalog: 'app_mobile'})},
      ],
      impersonateUser: null
    }
  },
  computed: {
    preferredLipidsUnit: {
      get() {
        return this.$store.getters[`user/preferredLipidsUnit`];
      },
      set(preferredLipidsUnit) {
        this.$store.dispatch('user/setSavePreferredLipidsUnit', {
          preferredLipidsUnit : preferredLipidsUnit,
          storeBackEnd : true
        }).then(() => {
          if (this.$store.state?.patientSummary?.user?.patient.patientId){
            this.$store.dispatch('patientSummary/update')
          }
        })
      },
    },
    preferredGlucoseUnit: {
      get() {
        return this.$store.getters[`user/preferredGlucoseUnit`];
      },
      set(preferredGlucoseUnit) {
        this.$store.dispatch('user/setSavePreferredGlucoseUnit', {
          preferredGlucoseUnit : preferredGlucoseUnit,
          storeBackEnd : true
        }).then(() => {
          if (this.$store.state?.patientSummary?.user?.patient.patientId){
            this.$store.dispatch('patientSummary/update')
          }
        })
      },
    },
  },
  methods: {
    impersonate () {
      return this.$app.authentication.impersonate(this.impersonateUser).then((loginData) => {
        if (loginData.success) {
          this.$toasted.show(this.$t('login.loggedin', {catalog: 'local'}))
          this.$router.push({name: 'Alerts'})
        } else {
          if (loginData.errorMessage)
            this.$toasted.error(this.$ucfirst(this.$t(loginData.errorMessage, {catalog: 'local'})))
        }
      })
    },
    /**
     * Navigates user to a previous page.
     */
    goToPreviousPage() {
      if (this.patientRecordId) {
        this.$router.push({
          name: 'PatientRecordOverview',
          params: {
            patientId: this.patientRecordId
          }
        })
      } else {
        this.$router.push({name: 'Alerts'})
      }
    },
    /**
     * Gets text to display on a button
     */
    getPreviousButtonText() {
      return this.$ucfirst(this.$t('buttons.backTo', {
        catalog: 'app_mobile',
        params: {
          'name': this.patientRecordId
              ? this.$t('patient.patientRecord').toLowerCase()
              : this.$t('patient.alerts').toLowerCase()
        }
      }))
    },
    /**
     * Saves patient ID in local storage before page is being reloded
     */
    onChange() {
      const patientId = this.$store.getters['followupSession/patientId'];
      if (patientId) {
        // save id to local storage before page reload
        window.localStorage.setItem('openedRecordPatientId', patientId)
      }
    },

    /**
     * Restores session after page reload if id was saved
     */
    async setPatientId() {
      let patientId = '';
      patientId = this.$store.getters['followupSession/patientId'];

      if (!patientId) {
        patientId = window.localStorage.getItem('openedRecordPatientId') || null;
        window.localStorage.removeItem('openedRecordPatientId')
        // restore session if id was saved
        if (patientId) {
          await this.$store.dispatch('followupSession/restore', patientId);

          const patient = {
            text: this.$store.getters['patientSummary/searchText'],
            patientId: this.$store.getters['patientSummary/id'],
          };

          if (patient.patientId) {
            this.$store.dispatch('patientList/selectPatient', patient);
          }
        }
      }
      this.patientRecordId = patientId;
    }
  },
  mounted() {
    this.setPatientId();
  }
};
</script>
